/**
 * Serviço para cálculos nutricionais (Web)
 * Calcula TMB, GET e distribuição de macronutrientes
 */

export type CalculationProtocol = 'mifflin_st_jeor' | 'harris_benedict' | 'katch_mcardle' | 'fao_who';
export type PersonType = 'adult' | 'child' | 'elderly' | 'pregnant' | 'athlete';

export interface NutritionCalculation {
  bmr: number; // Taxa Metabólica Basal (kcal)
  tdee: number; // Total Daily Energy Expenditure (kcal)
  targetCalories: number; // Calorias alvo baseado no objetivo
  macros: {
    protein: number; // gramas
    carbohydrates: number; // gramas
    fats: number; // gramas
  };
  macrosPercent: {
    protein: number; // percentual
    carbohydrates: number; // percentual
    fats: number; // percentual
  };
  waterIntake: number; // ml por dia
  protocol?: CalculationProtocol;
  protocolName?: string;
  bmrByProtocol?: Record<CalculationProtocol, number>;
  personType?: PersonType;
  personTypeName?: string;
}

export interface PatientData {
  weight: number; // kg
  height: number; // cm
  age: number; // anos
  gender: 'male' | 'female';
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
  goal: 'weight_loss' | 'muscle_gain' | 'health' | 'performance' | 'body_composition' | 'maintenance';
  personType?: PersonType;
  pregnancyTrimester?: 1 | 2 | 3; // apenas para gestantes
}

/** Normaliza objetivos do perfil do paciente para as opções aceitas pelo cálculo. */
export function mapPatientGoalToCalculationGoal(value: unknown): PatientData['goal'] | undefined {
  const rawGoal = Array.isArray(value) ? value.join(' ') : String(value ?? '');
  const goal = rawGoal
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .trim();

  if (!goal) return undefined;
  if (['weight loss', 'muscle gain', 'health', 'performance', 'body composition', 'maintenance'].includes(goal)) {
    return goal.replace(/ /g, '_') as PatientData['goal'];
  }
  if (/composicao corporal|hipertrofia.*(gordura|definicao)|reducao.*gordura/.test(goal)) {
    return 'body_composition';
  }
  if (/emagrec|perda de peso|weight loss|perder peso/.test(goal)) return 'weight_loss';
  if (/hipertrofia|ganho de massa|muscle gain|massa muscular/.test(goal)) return 'muscle_gain';
  if (/performance|desempenho/.test(goal)) return 'performance';
  if (/saude|health/.test(goal)) return 'health';
  if (/manutenc|maintenance/.test(goal)) return 'maintenance';

  return undefined;
}

/**
 * Retorna o nome do protocolo
 */
export function getProtocolName(protocol: CalculationProtocol): string {
  const names: Record<CalculationProtocol, string> = {
    mifflin_st_jeor: 'Mifflin-St Jeor',
    harris_benedict: 'Harris-Benedict (Revisado)',
    katch_mcardle: 'Katch-McArdle',
    fao_who: 'FAO/WHO',
  };
  return names[protocol] || 'Mifflin-St Jeor';
}

/**
 * Retorna a descrição e indicação clínica do protocolo
 */
export function getProtocolDescription(protocol: CalculationProtocol): string {
  const descriptions: Record<CalculationProtocol, string> = {
    mifflin_st_jeor: 'PADRÃO OURO (Academy of Nutrition and Dietetics). É atualmente a equação preditiva mais confiável para adultos não hospitalizados, abrangendo eutróficos, sobrepeso e obesidade. Assume uma composição corporal média.',
    harris_benedict: 'CLÁSSICA (Revisão 1984). Tende a superestimar a TMB em 5-15%. Mais indicada para indivíduos ativos ou quando se deseja evitar subestimação calórica. Menos precisa para obesidade grau II/III e sedentarismo extremo.',
    katch_mcardle: 'ALTA PRECISÃO (Requer % Gordura). Única fórmula que ignora o peso total e foca na Massa Livre de Gordura. Indispensável para atletas, fisiculturistas ou indivíduos com composição corporal atípica (muita massa muscular ou sarcopenia).',
    fao_who: 'POPULACIONAL (OMS 1985). Frequentemente usada como referência global e para crianças/adolescentes. Em adultos sedentários modernos, pode apresentar leve superestimação comparada a Mifflin-St Jeor.',
  };
  return descriptions[protocol] || '';
}

/**
 * Recomenda o melhor protocolo baseado no perfil do paciente
 */
export function getRecommendedProtocol(patient: PatientData): { protocol: CalculationProtocol; reason: string } {
  // 1. Atletas e Muito Ativos
  if (patient.activityLevel === 'very_active' || patient.personType === 'athlete') {
    return {
      protocol: 'katch_mcardle',
      reason: 'Paciente com alto nível de atividade/atleta. A fórmula de Katch-McArdle é a mais indicada pois considera a composição corporal (Massa Livre de Gordura), evitando erros de estimativa comuns em indivíduos com muita massa muscular.'
    };
  }

  // 2. Crianças e Adolescentes (< 18 anos)
  if (patient.age < 18 || patient.personType === 'child') {
    return {
      protocol: 'fao_who',
      reason: 'Para crianças e adolescentes, as equações da FAO/WHO são as mais validadas internacionalmente para acompanhar as necessidades energéticas durante a fase de crescimento e desenvolvimento.'
    };
  }

  // Calculando IMC para decisão clínica em adultos
  const heightM = patient.height / 100;
  const bmi = patient.weight / (heightM * heightM);

  // 3. Obesidade (IMC >= 30)
  if (bmi >= 30) {
    return {
      protocol: 'mifflin_st_jeor',
      reason: 'Para pacientes com obesidade (IMC ≥ 30), Mifflin-St Jeor é a recomendação clínica padrão ouro, pois apresenta a menor taxa de superestimação calórica comparada a Harris-Benedict.'
    };
  }

  // 4. Idosos
  if (patient.age >= 65 || patient.personType === 'elderly') {
    return {
      protocol: 'mifflin_st_jeor',
      reason: 'Para idosos, Mifflin-St Jeor é geralmente preferível por ser mais conservadora e ponderada, ajudando a evitar excessos que poderiam agravar comorbidades, embora Harris-Benedict também seja aceitável se o idoso for ativo.'
    };
  }

  // 5. Adulto Padrão (Eutrófico ou Sobrepeso)
  return {
    protocol: 'mifflin_st_jeor',
    reason: 'Mifflin-St Jeor é a escolha mais segura e precisa para a maioria dos adultos (eutróficos e sobrepeso), sendo a referência atual da Academy of Nutrition and Dietetics.'
  };
}

/**
 * Retorna o nome do tipo de pessoa
 */
export function getPersonTypeName(personType: PersonType): string {
  const names: Record<PersonType, string> = {
    adult: 'Adulto',
    child: 'Criança/Adolescente',
    elderly: 'Idoso',
    pregnant: 'Gestante',
    athlete: 'Atleta',
  };
  return names[personType] || 'Adulto';
}

/**
 * Detecta automaticamente o tipo de pessoa baseado nas características
 */
export function detectPersonType(
  age: number,
  gender: 'male' | 'female',
  activityLevel: string,
  pregnancyTrimester?: number
): PersonType {
  // Gestante tem prioridade se trimestre for informado
  if (pregnancyTrimester && pregnancyTrimester >= 1 && pregnancyTrimester <= 3) {
    return 'pregnant';
  }

  // Criança/Adolescente: idade < 18 anos
  if (age < 18) {
    return 'child';
  }

  // Idoso: idade >= 65 anos
  if (age >= 65) {
    return 'elderly';
  }

  // Atleta: nível de atividade muito alto (pode ser selecionado manualmente)
  // Por padrão, não detectamos automaticamente como atleta
  // pois requer conhecimento do tipo de esporte e intensidade
  // O usuário pode selecionar manualmente se necessário

  // Adulto padrão
  return 'adult';
}

/**
 * Calcula TMB para crianças usando Schofield (1985)
 */
export function calculateBMRForChild(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female'
): number {
  // Schofield (1985) - fórmulas por faixa etária
  if (age < 3) {
    // 0-3 anos
    if (gender === 'male') {
      return (59.512 * weight) - 30.4;
    } else {
      return (58.317 * weight) - 31.1;
    }
  } else if (age < 10) {
    // 3-10 anos
    if (gender === 'male') {
      return (22.706 * weight) + (504.3 * height / 100) + 172.9;
    } else {
      return (20.315 * weight) + (485.9 * height / 100) + 102.3;
    }
  } else if (age < 18) {
    // 10-18 anos
    if (gender === 'male') {
      return (17.686 * weight) + (658.2 * height / 100) + 371.2;
    } else {
      return (13.384 * weight) + (692.6 * height / 100) + 88.317;
    }
  }

  // Fallback para adolescentes próximos de 18 anos
  return calculateBMRForAdult(weight, height, age, gender, 'mifflin_st_jeor');
}

/**
 * Calcula TMB para idosos (ajustes específicos)
 */
export function calculateBMRForElderly(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female',
  protocol: CalculationProtocol = 'mifflin_st_jeor'
): number {
  // Usa fórmula padrão mas com ajuste para perda de massa muscular
  const baseBMR = calculateBMRForAdult(weight, height, age, gender, protocol);

  // Ajuste de -5% a -10% para idosos devido à perda de massa muscular
  // Ajuste progressivo baseado na idade
  const ageAdjustment = age >= 75 ? 0.90 : 0.95; // -10% para 75+, -5% para 65-74

  return baseBMR * ageAdjustment;
}

/**
 * Calcula TMB para gestantes
 */
export function calculateBMRForPregnant(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female',
  protocol: CalculationProtocol = 'mifflin_st_jeor',
  trimester: 1 | 2 | 3 = 1
): number {
  // Calcula TMB base (adulto)
  const baseBMR = calculateBMRForAdult(weight, height, age, gender, protocol);

  // Ajustes por trimestre (IOM - Institute of Medicine)
  const trimesterAdjustments: Record<1 | 2 | 3, number> = {
    1: 0,      // 1º trimestre: sem ajuste adicional
    2: 340,    // 2º trimestre: +340 kcal
    3: 452,    // 3º trimestre: +452 kcal
  };

  return baseBMR + trimesterAdjustments[trimester];
}

/**
 * Calcula TMB para atletas
 */
export function calculateBMRForAthlete(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female',
  protocol: CalculationProtocol = 'mifflin_st_jeor'
): number {
  // Atletas usam fórmulas padrão (Mifflin-St Jeor é recomendado)
  // A diferença está nos fatores de atividade que são mais altos
  return calculateBMRForAdult(weight, height, age, gender, protocol);
}

/**
 * Calcula TMB para adultos (função auxiliar)
 */
export function calculateBMRForAdult(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female',
  protocol: CalculationProtocol = 'mifflin_st_jeor'
): number {
  switch (protocol) {
    case 'mifflin_st_jeor':
      if (gender === 'male') {
        return 10 * weight + 6.25 * height - 5 * age + 5;
      } else {
        return 10 * weight + 6.25 * height - 5 * age - 161;
      }

    case 'harris_benedict':
      if (gender === 'male') {
        return 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
      } else {
        return 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
      }

    case 'katch_mcardle':
      const leanBodyMass = weight * 0.85;
      return 370 + (21.6 * leanBodyMass);

    case 'fao_who':
      if (gender === 'male') {
        if (age <= 30) {
          return (15.3 * weight) + 679;
        } else if (age <= 60) {
          return (11.6 * weight) + 879;
        } else {
          return (13.5 * weight) + 487;
        }
      } else {
        if (age <= 30) {
          return (14.7 * weight) + 496;
        } else if (age <= 60) {
          return (8.7 * weight) + 829;
        } else {
          return (10.5 * weight) + 596;
        }
      }

    default:
      if (gender === 'male') {
        return 10 * weight + 6.25 * height - 5 * age + 5;
      } else {
        return 10 * weight + 6.25 * height - 5 * age - 161;
      }
  }
}

/**
 * Calcula a Taxa Metabólica Basal (TMB) usando diferentes protocolos
 * Agora suporta diferentes tipos de pessoas
 */
export function calculateBMR(
  weight: number,
  height: number,
  age: number,
  gender: 'male' | 'female',
  protocol: CalculationProtocol = 'mifflin_st_jeor',
  personType?: PersonType,
  pregnancyTrimester?: 1 | 2 | 3
): number {
  // Se personType não for fornecido, detecta automaticamente
  const detectedType = personType || detectPersonType(age, gender, 'moderate', pregnancyTrimester);

  switch (detectedType) {
    case 'child':
      return calculateBMRForChild(weight, height, age, gender);

    case 'elderly':
      return calculateBMRForElderly(weight, height, age, gender, protocol);

    case 'pregnant':
      return calculateBMRForPregnant(weight, height, age, gender, protocol, pregnancyTrimester || 1);

    case 'athlete':
      return calculateBMRForAthlete(weight, height, age, gender, protocol);

    case 'adult':
    default:
      return calculateBMRForAdult(weight, height, age, gender, protocol);
  }
}

/**
 * Calcula o fator de atividade física
 * Ajustado para atletas que podem ter fatores mais altos
 */
export function getActivityFactor(activityLevel: string, personType?: PersonType): number {
  const baseFactors: Record<string, number> = {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
  };

  let factor = baseFactors[activityLevel] || 1.2;

  // Atletas podem ter fatores de atividade mais altos
  if (personType === 'athlete') {
    if (activityLevel === 'very_active') {
      factor = 2.0; // Atletas de alta intensidade
    } else if (activityLevel === 'active') {
      factor = 1.9; // Atletas moderados
    }
  }

  // Crianças podem ter fatores ligeiramente diferentes
  if (personType === 'child') {
    // Fatores similares mas podem ser ajustados
    // Por enquanto mantemos os mesmos
  }

  return factor;
}

/**
 * Calcula o ajuste de calorias baseado no objetivo
 */
export function getCalorieAdjustment(goal: string): number {
  const adjustments: Record<string, number> = {
    weight_loss: -500,
    muscle_gain: 300,
    health: 0,
    performance: 200,
    body_composition: -300,
    maintenance: 0,
  };
  return adjustments[goal] || 0;
}


/**
 * Calcula as calorias diárias necessárias (alvo)
 */
export function calculateTargetCalories(
  tdee: number,
  goal: string,
  gender: 'male' | 'female'
): number {
  const adjustment = getCalorieAdjustment(goal);
  const targetCalories = Math.round(tdee + adjustment);
  const minCalories = gender === 'male' ? 1500 : 1200;
  return Math.max(targetCalories, minCalories);
}

/**
 * Calcula a distribuição de macronutrientes baseado no objetivo
 */
export function calculateMacronutrients(
  calories: number,
  goal: string,
  weight: number
): { protein: number; carbohydrates: number; fats: number } {
  let proteinPercent: number;
  let carbPercent: number;
  let fatPercent: number;

  switch (goal) {
    case 'weight_loss':
      proteinPercent = 0.35;
      carbPercent = 0.35;
      fatPercent = 0.30;
      break;
    case 'muscle_gain':
      proteinPercent = 0.30;
      carbPercent = 0.45;
      fatPercent = 0.25;
      break;
    case 'performance':
      proteinPercent = 0.25;
      carbPercent = 0.50;
      fatPercent = 0.25;
      break;
    case 'body_composition':
      proteinPercent = 0.30;
      carbPercent = 0.40;
      fatPercent = 0.30;
      break;
    case 'health':
    case 'maintenance':
    default:
      proteinPercent = 0.25;
      carbPercent = 0.45;
      fatPercent = 0.30;
      break;
  }

  const proteinCalories = calories * proteinPercent;

  const minProtein = weight * 1.6;
  const calculatedProtein = proteinCalories / 4;
  const finalProtein = Math.max(calculatedProtein, minProtein);

  const adjustedCalories = calories - (finalProtein * 4);
  const adjustedCarbCalories = adjustedCalories * (carbPercent / (carbPercent + fatPercent));
  const adjustedFatCalories = adjustedCalories * (fatPercent / (carbPercent + fatPercent));

  return {
    protein: Math.round(finalProtein),
    carbohydrates: Math.round(adjustedCarbCalories / 4),
    fats: Math.round(adjustedFatCalories / 9),
  };
}

/**
 * Calcula a necessidade hídrica diária
 */
export function calculateWaterIntake(
  weight: number,
  activityLevel: string
): number {
  let baseWater = weight * 35;
  const activityMultipliers: Record<string, number> = {
    sedentary: 1.0,
    light: 1.1,
    moderate: 1.2,
    active: 1.3,
    very_active: 1.4,
  };
  const multiplier = activityMultipliers[activityLevel] || 1.0;
  return Math.round(baseWater * multiplier);
}

/**
 * Calcula o Total Daily Energy Expenditure (TDEE/GET)
 * Atualizado para considerar tipo de pessoa
 */
export function calculateTDEE(
  bmr: number,
  activityLevel: string,
  personType?: PersonType
): number {
  const activityFactor = getActivityFactor(activityLevel, personType);
  return bmr * activityFactor;
}

/**
 * Calcula todos os valores nutricionais
 */
export function calculateNutrition(
  patientData: PatientData,
  protocol: CalculationProtocol = 'mifflin_st_jeor'
): NutritionCalculation {
  // Detecta ou usa o tipo de pessoa fornecido
  const personType = patientData.personType || detectPersonType(
    patientData.age,
    patientData.gender,
    patientData.activityLevel,
    patientData.pregnancyTrimester
  );

  const bmr = calculateBMR(
    patientData.weight,
    patientData.height,
    patientData.age,
    patientData.gender,
    protocol,
    personType,
    patientData.pregnancyTrimester
  );

  const tdee = calculateTDEE(bmr, patientData.activityLevel, personType);
  const targetCalories = calculateTargetCalories(
    tdee,
    patientData.goal,
    patientData.gender
  );

  const macros = calculateMacronutrients(
    targetCalories,
    patientData.goal,
    patientData.weight
  );

  const totalMacroCalories = (macros.protein * 4) + (macros.carbohydrates * 4) + (macros.fats * 9);
  const macrosPercent = {
    protein: Math.round((macros.protein * 4 / totalMacroCalories) * 100),
    carbohydrates: Math.round((macros.carbohydrates * 4 / totalMacroCalories) * 100),
    fats: Math.round((macros.fats * 9 / totalMacroCalories) * 100),
  };

  const waterIntake = calculateWaterIntake(
    patientData.weight,
    patientData.activityLevel
  );

  // Calcula BMR para todos os protocolos (usando tipo adulto para comparação)
  const bmrByProtocol: Record<CalculationProtocol, number> = {
    mifflin_st_jeor: calculateBMR(patientData.weight, patientData.height, patientData.age, patientData.gender, 'mifflin_st_jeor', personType, patientData.pregnancyTrimester),
    harris_benedict: calculateBMR(patientData.weight, patientData.height, patientData.age, patientData.gender, 'harris_benedict', personType, patientData.pregnancyTrimester),
    katch_mcardle: calculateBMR(patientData.weight, patientData.height, patientData.age, patientData.gender, 'katch_mcardle', personType, patientData.pregnancyTrimester),
    fao_who: calculateBMR(patientData.weight, patientData.height, patientData.age, patientData.gender, 'fao_who', personType, patientData.pregnancyTrimester),
  };

  return {
    bmr: Math.round(bmr),
    tdee: Math.round(tdee),
    targetCalories,
    macros,
    macrosPercent,
    waterIntake,
    protocol,
    protocolName: getProtocolName(protocol),
    bmrByProtocol,
    personType,
    personTypeName: getPersonTypeName(personType),
  };
}
